import { useState } from 'react';
import Countdown from 'react-countdown';
import { invalidateAuctionCharityQueryById } from '../schedule/auctionCharity.api';
import { AuctionCharityDto } from '../schedule/auctionCharity.dto';

const CharityAuctionCountdown = ({
  charityAuction,
  className,
  titleClassName,
  countDownClassName,
  showTitle = true,
  showScheduleTime,
}: {
  charityAuction: AuctionCharityDto;
  className?: string;
  titleClassName?: string;
  countDownClassName?: string;
  showTitle?: boolean;
  showScheduleTime?: boolean;
}) => {
  const scheduleTime = new Date(charityAuction.scheduleItemsAt).getTime();
  const startTime = new Date(charityAuction.startItemsAt).getTime();
  const endTime = new Date(charityAuction.endItemsAt).getTime();
  const [isWaitingForConfirmations, setIsWaitingForConfirmations] = useState(
    Date.now() < scheduleTime &&
      charityAuction.status !== 'closed' &&
      charityAuction.status !== 'cancelled',
  );
  const [isWaitingForStart, setIsWaitingForStart] = useState(
    Date.now() < startTime &&
      charityAuction.status !== 'closed' &&
      charityAuction.status !== 'cancelled',
  );
  const [isAuctionFinished, setIsAuctionFinished] = useState(
    Date.now() >= endTime ||
      charityAuction.status === 'closed' ||
      charityAuction.status === 'cancelled',
  );
  const isAuctionFinalizing = isAuctionFinished && charityAuction.status === 'items-scheduled';
  const isAuctionInProcess = !isWaitingForStart && !isAuctionFinished;
  const isAuctionStarting = isAuctionInProcess && charityAuction.status === 'created';
  const countDownClass = countDownClassName ?? 'text-primary text-lg font-bold';
  const titleClass = titleClassName ?? 'text-xs font-bold text-gray-8a';
  const renderer = ({
    days,
    hours,
    minutes,
    seconds,
    completed,
  }: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    completed: boolean;
  }) => {
    if (completed) return <span className={countDownClass}>Elapsed</span>;
    if (days > 0) {
      return (
        <span className={countDownClass}>
          {days}d {hours}h
        </span>
      );
    }
    if (hours > 0) {
      return (
        <span className={countDownClass}>
          {hours}h {minutes.toString().padStart(2, '0')}m
        </span>
      );
    }
    if (minutes > 0) {
      return (
        <span className={countDownClass}>
          {minutes.toString().padStart(2, '0')}m {seconds.toString().padStart(2, '0')}s
        </span>
      );
    }

    return <span className={countDownClass}>{seconds}s</span>;
  };

  return (
    <div className="flex flex-row gap-x-8">
      <div className={className ? className : 'flex flex-col justify-start items-start'}>
        {showTitle && (
          <span className={titleClass}>
            {isWaitingForStart ? 'Time to start' : isAuctionInProcess ? 'Time left' : 'Status'}
          </span>
        )}

        {!isAuctionStarting && (isWaitingForStart || isAuctionInProcess) && (
          <Countdown
            date={isWaitingForStart ? startTime : endTime}
            renderer={renderer}
            onComplete={() => {
              isWaitingForStart ? setIsWaitingForStart(false) : setIsAuctionFinished(true);
              setTimeout(() => {
                invalidateAuctionCharityQueryById(charityAuction.id);
              }, 3000);
            }}
          />
        )}
        {isAuctionStarting && <span className={countDownClass}>Starting...</span>}
        {isAuctionFinished && (
          <span className={countDownClass}>
            {isAuctionFinalizing ? 'Finalizing...' : 'Finished'}
          </span>
        )}
      </div>
      {showScheduleTime && isWaitingForConfirmations && (
        <div className={className ? className : 'flex flex-col justify-start items-start'}>
          <span className={titleClass}>Deadline for confirmations</span>

          <Countdown date={scheduleTime} renderer={renderer} />
        </div>
      )}
    </div>
  );
};

export default CharityAuctionCountdown;
